import React, { useEffect, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, UserPlus, Power, Star } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, Input, TextArea, Select } from "../../components/common/Primitives";
import { Modal, ModalFooter } from "../../components/common/Modal";
import Price from "../../components/common/Price";
import { useAuth } from "../../lib/AuthContext";
import api, { formatApiError } from "../../lib/api";

export default function AdminPlans() {
  const { user: me } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan doc or {} for new
  const [deleting, setDeleting] = useState(null);
  const [assigningPlan, setAssigningPlan] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/plans")
      .then((r) => setPlans(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = { startup: plans.filter((p) => p.audience === "startup"),
                     investor: plans.filter((p) => p.audience === "investor") };

  const canDelete = me?.role === "super_admin";

  const toggleActive = async (plan) => {
    try {
      await api.post(`/admin/plans/${plan.plan_id}/toggle-active`);
      toast.success(`${plan.name} ${plan.active ? "disabled" : "enabled"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell title="Plans & Pricing" subtitle="ADMIN · MEMBERSHIP">
      <Toaster richColors position="top-right" />
      <div className="flex items-center justify-between mb-5">
        <div className="text-xs text-ink-muted">
          Edit membership plans, add new tiers, or manually assign a plan to any user.
        </div>
        <Button variant="gold" onClick={() => setEditing({})} data-testid="plan-new-btn">
          <span className="inline-flex items-center gap-1.5"><Plus size={14} /> New plan</span>
        </Button>
      </div>

      {loading ? (
        <Card><div className="text-ink-muted text-sm">Loading…</div></Card>
      ) : (
        <div className="space-y-8">
          {["startup", "investor"].map((aud) => (
            <div key={aud}>
              <div className="overline mb-3">{aud === "startup" ? "Startup Plans" : "Investor Plans"}</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {grouped[aud].length === 0 && (
                  <Card><div className="text-ink-muted text-sm">No {aud} plans yet. Click &quot;New plan&quot; to add one.</div></Card>
                )}
                {grouped[aud].map((p) => (
                  <Card key={p.plan_id} testId={`plan-card-${p.plan_id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-heading text-ink text-lg truncate">{p.name}</div>
                        <div className="text-[10px] text-ink-muted font-mono uppercase tracking-wider">{p.plan_id}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {p.featured && <Pill tone="gold"><Star size={9} className="inline mr-0.5" /> FEATURED</Pill>}
                        <Pill tone={p.active ? "success" : "muted"}>{p.active ? "ACTIVE" : "INACTIVE"}</Pill>
                      </div>
                    </div>
                    {p.tagline && <div className="text-xs text-ink-soft mb-3">{p.tagline}</div>}
                    <div className="mb-3">
                      <Price
                        price={p.price_inr || 0}
                        originalPrice={p.original_price_inr}
                        size="md"
                        suffix="/ quarter"
                        testId={`plan-price-${p.plan_id}`}
                      />
                    </div>
                    <ul className="space-y-1 text-sm text-ink-soft mb-4">
                      {(p.features || []).map((f, i) => <li key={i}>• {f}</li>)}
                    </ul>
                    <div className="flex items-center gap-1 pt-3 border-t border-navy/10">
                      <button
                        onClick={() => setEditing(p)}
                        className="p-1.5 rounded text-ink-soft hover:bg-navy/5 hover:text-ink"
                        title="Edit"
                        data-testid={`plan-edit-${p.plan_id}`}
                      ><Pencil size={14} /></button>
                      <button
                        onClick={() => toggleActive(p)}
                        className="p-1.5 rounded text-ink-soft hover:bg-navy/5 hover:text-ink"
                        title={p.active ? "Disable" : "Enable"}
                        data-testid={`plan-toggle-${p.plan_id}`}
                      ><Power size={14} /></button>
                      <button
                        onClick={() => setAssigningPlan(p)}
                        className="p-1.5 rounded text-ink-soft hover:bg-navy/5 hover:text-ink"
                        title="Assign to user"
                        data-testid={`plan-assign-${p.plan_id}`}
                      ><UserPlus size={14} /></button>
                      <button
                        onClick={() => setDeleting(p)}
                        disabled={!canDelete}
                        className={`p-1.5 rounded ${canDelete ? "text-rose-600 hover:bg-rose-50" : "text-ink-faint cursor-not-allowed"}`}
                        title={canDelete ? "Delete" : "Only super_admin can delete"}
                        data-testid={`plan-delete-${p.plan_id}`}
                      ><Trash2 size={14} /></button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <PlanFormModal
          plan={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {deleting && (
        <DeletePlanModal
          plan={deleting}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); load(); }}
        />
      )}
      {assigningPlan && (
        <AssignPlanModal
          plan={assigningPlan}
          onClose={() => setAssigningPlan(null)}
          onDone={() => setAssigningPlan(null)}
        />
      )}
    </DashboardShell>
  );
}

// -----------------------------------------------------------------------
// Plan form modal (create + edit)
// -----------------------------------------------------------------------
function PlanFormModal({ plan, onClose, onSaved }) {
  const isEdit = !!plan.plan_id;
  const [form, setForm] = useState({
    plan_id: plan.plan_id || "",
    audience: plan.audience || "startup",
    name: plan.name || "",
    tagline: plan.tagline || "",
    price_inr: plan.price_inr ?? 0,
    original_price_inr: plan.original_price_inr ?? "",
    features_text: (plan.features || []).join("\n"),
    featured: !!plan.featured,
    active: plan.active === undefined ? true : !!plan.active,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const priceNum = Number(form.price_inr) || 0;
      const originalRaw = form.original_price_inr === "" ? null : Number(form.original_price_inr);
      if (originalRaw !== null && originalRaw < priceNum) {
        toast.error("Original price must be greater than the discounted price.");
        setSaving(false);
        return;
      }
      const body = {
        audience: form.audience,
        name: form.name.trim(),
        tagline: form.tagline.trim(),
        price_inr: priceNum,
        // Send 0 to clear the struck-off price on the server (Pydantic strips null on PATCH)
        original_price_inr: originalRaw && originalRaw > priceNum ? originalRaw : 0,
        features: form.features_text.split("\n").map((f) => f.trim()).filter(Boolean),
        featured: form.featured,
        active: form.active,
      };
      if (isEdit) {
        await api.put(`/admin/plans/${plan.plan_id}`, body);
        toast.success("Plan updated");
      } else {
        if (form.plan_id.trim()) body.plan_id = form.plan_id.trim();
        await api.post("/admin/plans", body);
        toast.success("Plan created");
      }
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? "Edit plan" : "New plan"} subtitle={isEdit ? plan.plan_id : "Create a new membership tier"} size="lg" testId="plan-form-modal">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select
            label="Audience"
            value={form.audience}
            onChange={(e) => setForm({ ...form, audience: e.target.value })}
            testId="plan-audience"
            disabled={isEdit}
          >
            <option value="startup">Startup</option>
            <option value="investor">Investor</option>
          </Select>
          <Input
            label="Plan ID (auto-generated if blank)"
            value={form.plan_id}
            onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
            disabled={isEdit}
            placeholder={`${form.audience}_${(form.name || "").toLowerCase().replace(/\s+/g, "_")}`}
            testId="plan-id"
          />
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            testId="plan-name"
          />
          <Input
            label="Price (₹)"
            type="number"
            min={0}
            value={form.price_inr}
            onChange={(e) => setForm({ ...form, price_inr: e.target.value })}
            testId="plan-price"
          />
          <Input
            label="Original price (₹) — optional, struck-off"
            type="number"
            min={0}
            value={form.original_price_inr}
            onChange={(e) => setForm({ ...form, original_price_inr: e.target.value })}
            placeholder="Leave blank for no discount"
            testId="plan-original-price"
          />
          <div className="md:col-span-2">
            <Input
              label="Tagline"
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              placeholder="One-liner shown under the plan name"
              testId="plan-tagline"
            />
          </div>
          <div className="md:col-span-2">
            <TextArea
              label="Features (one per line)"
              value={form.features_text}
              onChange={(e) => setForm({ ...form, features_text: e.target.value })}
              placeholder={"Priority review\nFeatured listing\nUp to 20 investor interests / month"}
              testId="plan-features"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 pt-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              data-testid="plan-featured"
              className="accent-navy"
            />
            Featured
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="plan-active"
              className="accent-navy"
            />
            Active
          </label>
        </div>
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="gold" disabled={saving} data-testid="plan-form-submit">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create plan"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// -----------------------------------------------------------------------
// Delete confirmation
// -----------------------------------------------------------------------
function DeletePlanModal({ plan, onClose, onDone }) {
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const ok = confirmText.trim() === plan.plan_id;

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/plans/${plan.plan_id}`);
      toast.success("Plan deleted");
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeleting(false); }
  };
  return (
    <Modal open onClose={onClose} title="Delete plan" subtitle={plan.name} size="sm" testId="plan-delete-modal">
      <div className="space-y-3">
        <div className="border border-rose-200 bg-rose-50 text-rose-900 text-sm p-3 rounded">
          This will permanently delete the plan. If any users are currently on it, the API will block the delete.
        </div>
        <Input
          label={`Type "${plan.plan_id}" to confirm`}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          testId="plan-delete-confirm"
        />
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={doDelete} disabled={!ok || deleting} data-testid="plan-delete-submit">
            {deleting ? "Deleting…" : "Delete permanently"}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// -----------------------------------------------------------------------
// Assign-to-user modal
// -----------------------------------------------------------------------
function AssignPlanModal({ plan, onClose, onDone }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/admin/users?q=${encodeURIComponent(query)}&role=${plan.audience}`);
        setResults(r.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, plan.audience]);

  const submit = async () => {
    if (!selected) { toast.error("Pick a user first"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/users/${selected.user_id}/assign-plan`, {
        plan_id: plan.plan_id,
        note,
      });
      toast.success(`${plan.name} assigned to ${selected.email}`);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Assign: ${plan.name}`} subtitle={`${plan.audience.toUpperCase()} · ₹${Number(plan.price_inr).toLocaleString("en-IN")}`} size="md" testId="plan-assign-modal">
      <div className="space-y-3">
        <Input
          label={`Search ${plan.audience} users`}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
          placeholder="Name or email…"
          testId="plan-assign-search"
        />
        {searching && <div className="text-xs text-ink-muted">Searching…</div>}
        {!selected && results.length > 0 && (
          <div className="border border-navy/10 rounded max-h-64 overflow-y-auto divide-y divide-navy/5">
            {results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => setSelected(u)}
                className="w-full text-left px-3 py-2 hover:bg-navy/[0.03] flex items-center justify-between"
                data-testid={`plan-assign-user-${u.user_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate">{u.name || "—"}</div>
                  <div className="text-xs text-ink-muted truncate">{u.email}</div>
                </div>
                <Pill tone="muted">{u.role}</Pill>
              </button>
            ))}
          </div>
        )}
        {selected && (
          <div className="border border-gold/50 bg-gold/10 rounded p-3 flex items-center justify-between">
            <div>
              <div className="text-sm text-ink font-medium">{selected.name}</div>
              <div className="text-xs text-ink-muted">{selected.email} · {selected.role}</div>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-xs text-ink-muted hover:underline"
              data-testid="plan-assign-clear"
            >Change</button>
          </div>
        )}
        <TextArea
          label="Note (optional — reason, discount, etc.)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          testId="plan-assign-note"
        />
        <div className="text-[11px] text-ink-muted">
          🎁 This is a manual assignment (no Cashfree charge). Perfect for comps, waivers, and enterprise deals. The action is logged in the audit trail.
        </div>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="gold" onClick={submit} disabled={saving || !selected} data-testid="plan-assign-submit">
            {saving ? "Assigning…" : "Assign plan"}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
